import type { ChangeEvent } from 'react';

import { getUserTimezone, setUserTimezone } from '@/utils/formatDate';

type Props = {
  /** Called after the user picks a timezone. Default reloads the page. */
  onChange?: (tz: string) => void;
};

const GROUPS: Array<{ label: string; options: Array<{ value: string; label: string }> }> = [
  {
    label: 'Americas',
    options: [
      { value: 'America/New_York', label: 'US Eastern' },
      { value: 'America/Chicago', label: 'US Central' },
      { value: 'America/Denver', label: 'US Mountain' },
      { value: 'America/Los_Angeles', label: 'US Pacific' },
      { value: 'America/Sao_Paulo', label: 'Brazil (SP)' },
      { value: 'America/Mexico_City', label: 'Mexico City' },
      { value: 'America/Bogota', label: 'Colombia' },
      { value: 'America/Argentina/Buenos_Aires', label: 'Argentina' },
    ],
  },
  {
    label: 'Europe',
    options: [
      { value: 'Europe/London', label: 'London (GMT)' },
      { value: 'Europe/Paris', label: 'Paris (CET)' },
      { value: 'Europe/Berlin', label: 'Berlin (CET)' },
      { value: 'Europe/Madrid', label: 'Madrid (CET)' },
      { value: 'Europe/Lisbon', label: 'Lisbon (WET)' },
    ],
  },
  {
    label: 'Asia / Middle East',
    options: [
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
      { value: 'Asia/Dubai', label: 'Dubai (GST)' },
      { value: 'Asia/Kolkata', label: 'India (IST)' },
      { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    ],
  },
];

export function TimezoneSelect({ onChange }: Props) {
  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const tz = e.target.value;
    setUserTimezone(tz);
    if (onChange) onChange(tz);
    else window.location.reload();
  };

  return (
    <label className="mt-1 block">
      <span className="sr-only">Display timezone</span>
      <select
        defaultValue={getUserTimezone() ?? ''}
        onChange={handleChange}
        className="w-full rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-300"
        title="Your display timezone"
      >
        <option value="">Project timezone</option>
        {GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
